---
title: Projects and logs
lede: Start and stop each project of a session, and read everything it printed.
order: 19
part: Run a task
---

The dock's **Projects & logs** pane lists the session's projects and shows their output. Open the dock with `⌥⌘J`, or with the first button of the status bar.

![The dock tabs](media/dock-tabs.webp)

The dock holds three kinds of tab: **Projects & logs**, **Agent MCP** and one or more [VM shells](/vm-shell). Drag a tab to reorder them. The buttons at the far right act on the output and on the dock itself.

### Starting and stopping

![Projects and logs](media/dock-projects.webp)

Each row runs or stops its own project, and shows its port once the health check answers. **Start all** and **Stop all** sit under the list; the Session menu has the same two, with **Start all projects** on `⌘⌥R`.

A project with no run command has nothing to start, and sits under **No run command** below the others: give it one in [Workspace settings › Repositories](/repositories). On a session placed on this computer, a port already taken is swapped for a free one, and the row shows the port given (dotted, with the reason in its tooltip).

### Publishing a project

The globe on a row opens a public URL onto that project through a [tunnel](/tunnels). While it is open, the row shows the address; click it to copy. The globe appears only when the workspace allows tunnels, ngrok is set up, and the project declares a port.

### Reading the output

The **All** row shows every project's output, coloured by project. Click a project to show only its output, and ⇧-click another to add it. Lines from the dependency install are marked `install`, and values a [live probe](/live-probes) captured are marked `probe`.

**Filter the output** narrows the pane to a piece of text, or to a regular expression with the `.*` toggle, and counts the lines it shows. `⌘F` (**Edit › Find in logs**) opens it.

### Noting a line

Select lines of output, or use **Note this line** on one, and write what is wrong. The note goes to the agent as soon as you press **Send**, with the lines quoted.

### The dock's buttons

| Button | What it does |
|---|---|
| **Filter the output** | Narrow the output to text or a pattern |
| **Export the output** | Write everything the session printed to a file |
| **Clear the output** | Empty the pane; the files keep everything |
| **Take the whole scene** | Give the dock the stage's full height; press again to give it back |
| **Hide the dock** | Close the dock |

The first three appear only on **Projects & logs**.

### Retention

![Log settings](media/settings-logs.webp)

**Settings › Logs** sets two limits, and a workspace can override both in its own **Logs** page.

| Setting | Default | What it limits |
|---|---|---|
| **Retention per log file** | 256 MB | How large one log file grows before it is truncated |
| **Lines kept in the logs dock** | 2,000 | Lines the pane holds per project |

Lines the pane drops stay in the files and their search index, and the pane says when its beginning was dropped.
